/**
 * Turns a photo staff pick from their files into the image Payabli's check
 * scanner expects — the same thing its camera would have handed it.
 *
 * The camera path (`captureImageInModal` in Payabli's component.js) draws a
 * 2:1 crop of the video frame onto a 1280×640 canvas and sends
 * `canvas.toDataURL('image/jpeg', 0.92)`. A photo from disk has no fixed
 * shape, so it is fitted INSIDE that frame, aspect ratio kept, on a dark
 * background — stretching it would distort the MICR line the reader needs.
 *
 * Payabli refuses an image over 1 MB, so the quality steps down until the
 * JPEG fits. The image never leaves the browser from here: it goes back to
 * the component, which sends it to Payabli itself.
 *
 * iPhone photos are HEIC. Safari decodes those itself; Chrome, Firefox and
 * Edge cannot, so a HEIC file the browser refuses is converted with libheif
 * (WebAssembly, `heic-to`), fetched only when such a file is chosen — it is
 * several MB and most staff never need it.
 *
 * The photo is drawn the way it was taken (EXIF orientation applied). A
 * portrait phone shot usually has the check lying across it already, so
 * nothing is turned automatically; staff turn it themselves in the preview
 * (`quarterTurns`) when the check was photographed on its side.
 */

export const CHECK_IMAGE_WIDTH = 1280;
export const CHECK_IMAGE_HEIGHT = 640;
/** What the letterbox around a photo is filled with. */
export const CHECK_IMAGE_BACKGROUND = '#222';
/** Under Payabli's 1 MB per-image limit, with room to spare. */
export const CHECK_IMAGE_MAX_BYTES = 950 * 1024;
/** Tried in order; the camera path's own quality comes first. */
export const CHECK_IMAGE_QUALITIES = [0.92, 0.85, 0.75, 0.65] as const;

export const UNSUPPORTED_CHECK_IMAGE_MESSAGE = 'Use a JPEG or PNG photo.';
/** A HEIC/HEIF photo that neither the browser nor the converter could read. */
export const UNREADABLE_HEIC_MESSAGE = "We couldn't read this photo. Try a JPEG or PNG.";

/**
 * What the file picker offers. `image/*` alone leaves HEIC out of some
 * pickers (Chrome on Windows and Linux does not map it to an image type).
 */
export const CHECK_IMAGE_ACCEPT = 'image/*,image/heic,image/heif,.heic,.heif';

const HEIC_MIME_TYPES = new Set(['image/heic', 'image/heif', 'image/heic-sequence']);
const HEIC_EXTENSION = /\.(heic|heif)$/i;
/** `ftyp` major brands of HEIC/HEIF files (ISO/IEC 23008-12). */
const HEIC_BRANDS = new Set(['heic', 'heix', 'hevc', 'heim', 'heis', 'mif1', 'msf1']);
export const OVERSIZED_CHECK_IMAGE_MESSAGE = 'This photo is too large to send. Try another photo.';

/** A photo that cannot be used; `message` is what staff are shown. */
export class CheckImageError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CheckImageError';
  }
}

/** Where a `width`×`height` image lands when fitted inside the box, centred. */
export function fitWithin(
  width: number,
  height: number,
  boxWidth = CHECK_IMAGE_WIDTH,
  boxHeight = CHECK_IMAGE_HEIGHT,
): { x: number; y: number; width: number; height: number } {
  const scale = Math.min(boxWidth / width, boxHeight / height);
  const fittedWidth = Math.round(width * scale);
  const fittedHeight = Math.round(height * scale);
  return {
    x: Math.round((boxWidth - fittedWidth) / 2),
    y: Math.round((boxHeight - fittedHeight) / 2),
    width: fittedWidth,
    height: fittedHeight,
  };
}

/** Bytes the base64 payload of a data URL decodes to. */
export function dataUrlByteLength(dataUrl: string): number {
  const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
  return Math.floor((base64.length * 3) / 4) - padding;
}

function readBytes(blob: Blob): Promise<ArrayBuffer> {
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsArrayBuffer(blob);
  });
}

/**
 * True for a HEIC/HEIF photo. Files often arrive with no MIME type at all
 * (Chrome on Windows, drag-and-drop), so the extension and then the file's
 * own `ftyp` brand are checked too.
 */
export async function isHeicFile(file: Blob): Promise<boolean> {
  if (HEIC_MIME_TYPES.has(file.type.toLowerCase())) return true;
  const name = (file as Partial<File>).name;
  if (typeof name === 'string' && HEIC_EXTENSION.test(name)) return true;
  try {
    const head = new Uint8Array(await readBytes(file.slice(0, 12)));
    if (head.length < 12) return false;
    const ascii = (from: number) => String.fromCharCode(...head.subarray(from, from + 4));
    return ascii(4) === 'ftyp' && HEIC_BRANDS.has(ascii(8));
  } catch {
    return false;
  }
}

/** The libheif converter, loaded on first use into a chunk of its own. */
export function loadHeicConverter() {
  // The `csp` build: no `eval`/`new Function`, so no `unsafe-eval` needed.
  // LGPL-3.0, shipped unmodified as its own chunk; its notice and licences
  // are published at /third-party/heic-to/ (public/third-party/heic-to).
  return import('heic-to/csp');
}

type Decoded = {
  source: CanvasImageSource;
  width: number;
  height: number;
  release: () => void;
};

async function decodeWithBitmap(file: Blob): Promise<Decoded | null> {
  if (typeof createImageBitmap !== 'function') return null;
  try {
    // EXIF orientation applied, so a phone photo comes out upright.
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    return {
      source: bitmap,
      width: bitmap.width,
      height: bitmap.height,
      release: () => bitmap.close(),
    };
  } catch {
    return null;
  }
}

/** An <img> applies the photo's EXIF orientation itself (`image-orientation: from-image`). */
function decodeWithImageElement(file: Blob): Promise<Decoded | null> {
  const url = URL.createObjectURL(file);
  return new Promise<Decoded | null>((resolve) => {
    const image = new Image();
    image.onload = () => {
      resolve(
        image.naturalWidth > 0 && image.naturalHeight > 0
          ? {
              source: image,
              width: image.naturalWidth,
              height: image.naturalHeight,
              release: () => {},
            }
          : null,
      );
    };
    image.onerror = () => resolve(null);
    image.src = url;
  }).finally(() => URL.revokeObjectURL(url));
}

/** Quarter turns clockwise: 0 (as taken), 1, 2 (upside down) or 3. */
export type QuarterTurns = 0 | 1 | 2 | 3;

/** Draw the photo turned `quarterTurns` clockwise, fitted inside the frame. */
function drawTurned(
  context: CanvasRenderingContext2D,
  decoded: Decoded,
  quarterTurns: QuarterTurns,
) {
  if (quarterTurns === 0) {
    const box = fitWithin(decoded.width, decoded.height);
    context.drawImage(decoded.source, box.x, box.y, box.width, box.height);
    return;
  }
  // A quarter or three-quarter turn swaps the photo's width and height.
  const sideways = quarterTurns % 2 === 1;
  const box = sideways
    ? fitWithin(decoded.height, decoded.width)
    : fitWithin(decoded.width, decoded.height);
  const drawWidth = sideways ? box.height : box.width;
  const drawHeight = sideways ? box.width : box.height;
  context.save();
  context.translate(box.x + box.width / 2, box.y + box.height / 2);
  context.rotate((quarterTurns * Math.PI) / 2);
  context.drawImage(decoded.source, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);
  context.restore();
}

/** libheif applies the file's own rotation/mirroring, so this is upright too. */
async function decodeHeic(file: Blob): Promise<Decoded | null> {
  try {
    const { heicTo } = await loadHeicConverter();
    const bitmap = await heicTo({ blob: file, type: 'bitmap' });
    return {
      source: bitmap,
      width: bitmap.width,
      height: bitmap.height,
      release: () => bitmap.close(),
    };
  } catch {
    return null;
  }
}

export interface PrepareCheckImageOptions {
  /** A HEIC photo is being converted: that takes a few seconds. */
  onConverting?: () => void;
  /** Turn the photo clockwise this many quarter turns. Default: as taken. */
  quarterTurns?: QuarterTurns;
}

/**
 * Decode `file` and return the JPEG data URL the scanner is to be given.
 * Throws a `CheckImageError` for a file that cannot be decoded (a PDF, a
 * damaged HEIC) or one that will not fit under the size limit.
 */
export async function prepareCheckImage(
  file: Blob,
  { onConverting, quarterTurns = 0 }: PrepareCheckImageOptions = {},
): Promise<string> {
  // The browser's own decoder first: Safari reads HEIC natively.
  let decoded = (await decodeWithBitmap(file)) ?? (await decodeWithImageElement(file));
  if (!decoded && (await isHeicFile(file))) {
    onConverting?.();
    decoded = await decodeHeic(file);
    if (!decoded || decoded.width <= 0 || decoded.height <= 0) {
      decoded?.release();
      throw new CheckImageError(UNREADABLE_HEIC_MESSAGE);
    }
  }
  if (!decoded || decoded.width <= 0 || decoded.height <= 0) {
    decoded?.release();
    throw new CheckImageError(UNSUPPORTED_CHECK_IMAGE_MESSAGE);
  }

  const canvas = document.createElement('canvas');
  canvas.width = CHECK_IMAGE_WIDTH;
  canvas.height = CHECK_IMAGE_HEIGHT;
  const context = canvas.getContext('2d');
  if (!context) {
    decoded.release();
    throw new CheckImageError(UNSUPPORTED_CHECK_IMAGE_MESSAGE);
  }
  try {
    context.fillStyle = CHECK_IMAGE_BACKGROUND;
    context.fillRect(0, 0, CHECK_IMAGE_WIDTH, CHECK_IMAGE_HEIGHT);
    drawTurned(context, decoded, quarterTurns);
  } finally {
    decoded.release();
  }

  for (const quality of CHECK_IMAGE_QUALITIES) {
    const dataUrl = canvas.toDataURL('image/jpeg', quality);
    if (!dataUrl.startsWith('data:image/jpeg')) {
      throw new CheckImageError(UNSUPPORTED_CHECK_IMAGE_MESSAGE);
    }
    if (dataUrlByteLength(dataUrl) <= CHECK_IMAGE_MAX_BYTES) return dataUrl;
  }
  throw new CheckImageError(OVERSIZED_CHECK_IMAGE_MESSAGE);
}
